import { z } from 'zod'

/**
 * Recurrence: turning "every second Tuesday at 7pm" into actual instants.
 *
 * Written once here and shared by Groups, Events and Services (02-PHASES.md),
 * because three half-implementations of RFC 5545 is three sets of off-by-one
 * bugs at 7pm on a Tuesday.
 *
 * This is a deliberate *subset* of RFC 5545 — DAILY, WEEKLY and MONTHLY with
 * INTERVAL, BYDAY (including nth and last weekday), BYMONTHDAY, COUNT, UNTIL
 * and WKST. Not YEARLY, not BYSETPOS, not BYYEARDAY. Church schedules are
 * weekly services, fortnightly small groups and "the first Sunday of the
 * month"; the parts of the spec that exist for calendaring interop can be
 * added when something needs them.
 *
 * ## Wall time, not instants
 *
 * A series is stored as a *local* start time plus an IANA timezone, and each
 * occurrence is converted to UTC on expansion. That is what makes it DST-safe:
 * a 19:00 group meeting stays at 19:00 for the people attending it, even
 * though the UTC instant moves by an hour twice a year. Storing UTC and adding
 * seven days would drift the meeting to 18:00 or 20:00 every spring and
 * autumn — which is the single most common recurrence bug there is.
 */

export const WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const
export type Weekday = (typeof WEEKDAYS)[number]

/** `MO`, or `{ nth: 2, day: 'TU' }` for "second Tuesday" (`-1` = last). */
export type ByDay = Weekday | { nth: number; day: Weekday }

export interface RecurrenceRule {
  freq: 'DAILY' | 'WEEKLY' | 'MONTHLY'
  /** Every N periods. Default 1. */
  interval?: number
  byDay?: ByDay[]
  /** Days of the month; negative counts from the end (`-1` is the last day). */
  byMonthDay?: number[]
  /** Stop after this many occurrences. */
  count?: number
  /** Stop at this local date-time, inclusive (`YYYY-MM-DD` or `…THH:mm`). */
  until?: string
  /** Which day a week starts on, for INTERVAL arithmetic. Default MO. */
  weekStart?: Weekday
}

export interface RecurringSeries {
  /** Local wall time of the first occurrence: `YYYY-MM-DDTHH:mm`. */
  start: string
  durationMinutes: number
  /** IANA zone, e.g. `America/New_York`. */
  timeZone: string
  rule: RecurrenceRule
  /** Local dates or date-times to skip — RFC 5545's EXDATE. */
  exceptions?: string[]
}

export interface Occurrence {
  /** `YYYY-MM-DDTHH:mm` in the series' zone — what a human sees. */
  localStart: string
  /** The instant, for storage and comparison. */
  startsAt: Date
  endsAt: Date
}

// ---- timezone arithmetic ------------------------------------------------------

/** Offset of a zone at a given instant, in milliseconds. */
function zoneOffsetMs(utcMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs))

  const at = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0')
  // `hour` can format as 24 at midnight under hour12: false.
  const asIfUtc = Date.UTC(
    at('year'),
    at('month') - 1,
    at('day'),
    at('hour') % 24,
    at('minute'),
    at('second'),
  )
  return asIfUtc - utcMs
}

/**
 * The instant at which the given wall time occurs in a zone.
 *
 * Two passes: the first guess uses the offset at the *wrong* instant, the
 * second corrects it. That is what makes the hour around a DST change come out
 * right — a naive single pass lands an hour off on those two days a year.
 *
 * A wall time that does not exist (02:30 on spring-forward) resolves to the
 * instant the clock skips to, which is what a person expects from "the meeting
 * is at 2:30".
 */
export function zonedWallTimeToUtc(local: LocalDateTime, timeZone: string): Date {
  const asIfUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute)
  const firstGuess = asIfUtc - zoneOffsetMs(asIfUtc, timeZone)
  const corrected = asIfUtc - zoneOffsetMs(firstGuess, timeZone)

  // If the correction does not render back to the wall time asked for, that
  // wall time does not exist — it is inside the spring-forward gap. Correcting
  // again would land *before* the requested time (02:30 becoming 01:30), so
  // the pre-transition offset is used instead, which is the instant the clock
  // skips to. "The meeting is at 2:30" should never mean half past one.
  if (asIfUtc - zoneOffsetMs(corrected, timeZone) !== corrected) return new Date(firstGuess)
  return new Date(corrected)
}

export interface LocalDateTime {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

const LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?$/

export function parseLocal(value: string): LocalDateTime {
  const match = LOCAL_PATTERN.exec(value.trim())
  if (!match) throw new Error(`Not a local date-time: ${value}`)
  const [, year, month, day, hour, minute] = match
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour ?? '0'),
    minute: Number(minute ?? '0'),
  }
}

const pad = (value: number) => String(value).padStart(2, '0')

export const formatLocal = (local: LocalDateTime): string =>
  `${local.year}-${pad(local.month)}-${pad(local.day)}T${pad(local.hour)}:${pad(local.minute)}`

/** Day-of-week of a calendar date, without touching timezones. */
function weekdayOf(year: number, month: number, day: number): Weekday {
  const index = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  return WEEKDAYS[index] as Weekday
}

const daysInMonth = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate()

/** Calendar-day arithmetic on a local date, ignoring clocks entirely. */
function addDays(local: LocalDateTime, days: number): LocalDateTime {
  const shifted = new Date(Date.UTC(local.year, local.month - 1, local.day + days))
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: local.hour,
    minute: local.minute,
  }
}

function addMonths(local: LocalDateTime, months: number): LocalDateTime {
  const total = local.year * 12 + (local.month - 1) + months
  const year = Math.floor(total / 12)
  const month = (total % 12) + 1
  return { ...local, year, month, day: Math.min(local.day, daysInMonth(year, month)) }
}

// ---- expansion ----------------------------------------------------------------

const normalizeByDay = (entry: ByDay) =>
  typeof entry === 'string' ? { nth: 0, day: entry } : entry

/** Local dates in one month that match the rule's BYDAY / BYMONTHDAY. */
function monthlyDays(rule: RecurrenceRule, year: number, month: number): number[] {
  const days = new Set<number>()
  const length = daysInMonth(year, month)

  for (const raw of rule.byMonthDay ?? []) {
    const day = raw > 0 ? raw : length + raw + 1
    if (day >= 1 && day <= length) days.add(day)
  }

  for (const entry of rule.byDay ?? []) {
    const { nth, day } = normalizeByDay(entry)
    const matching: number[] = []
    for (let candidate = 1; candidate <= length; candidate++) {
      if (weekdayOf(year, month, candidate) === day) matching.push(candidate)
    }
    if (nth === 0) {
      for (const candidate of matching) days.add(candidate)
    } else {
      const picked = nth > 0 ? matching[nth - 1] : matching[matching.length + nth]
      if (picked) days.add(picked)
    }
  }

  return [...days].sort((a, b) => a - b)
}

/** Whole weeks between two dates, measured from the rule's week start. */
function weeksBetween(from: LocalDateTime, to: LocalDateTime, weekStart: Weekday): number {
  const startIndex = WEEKDAYS.indexOf(weekStart)
  const shift = (local: LocalDateTime) => {
    const index = WEEKDAYS.indexOf(weekdayOf(local.year, local.month, local.day))
    return (index - startIndex + 7) % 7
  }
  const fromWeek = Date.UTC(from.year, from.month - 1, from.day - shift(from))
  const toWeek = Date.UTC(to.year, to.month - 1, to.day - shift(to))
  return Math.round((toWeek - fromWeek) / (7 * 24 * 60 * 60 * 1000))
}

export interface ExpandOptions {
  /** Only occurrences at or after this instant. */
  from?: Date
  /** Only occurrences before this instant. */
  to?: Date
  /** Hard cap, so an open-ended rule cannot be asked for infinity. */
  limit?: number
}

/** Runaway guard: a daily rule with no COUNT/UNTIL over a wide window. */
const MAX_CANDIDATES = 20_000

/**
 * Expands a series into occurrences, in order.
 *
 * COUNT is counted from the series start — the RFC's meaning — so paging a
 * window in the middle still yields the right dates and stops at the right
 * one. Exceptions do not consume a COUNT slot, matching RFC 5545's EXDATE.
 */
export function expandSeries(series: RecurringSeries, options: ExpandOptions = {}): Occurrence[] {
  const rule = series.rule
  const interval = Math.max(1, rule.interval ?? 1)
  const weekStart = rule.weekStart ?? 'MO'
  const start = parseLocal(series.start)
  const until = rule.until ? parseLocal(rule.until) : null
  const limit = options.limit ?? 500

  const exceptions = new Set(
    (series.exceptions ?? []).map((value) => {
      const local = parseLocal(value)
      // A bare date excludes the whole day; a date-time excludes that instant.
      return value.includes('T') || value.includes(' ')
        ? formatLocal(local)
        : `${local.year}-${pad(local.month)}-${pad(local.day)}`
    }),
  )

  const results: Occurrence[] = []
  let produced = 0
  let candidates = 0

  const consider = (local: LocalDateTime): 'continue' | 'stop' => {
    if (until) {
      const untilKey = formatLocal(until)
      // A bare UNTIL date includes the whole day.
      const compare = series.rule.until?.includes('T')
        ? formatLocal(local) > untilKey
        : `${local.year}-${pad(local.month)}-${pad(local.day)}` >
          `${until.year}-${pad(until.month)}-${pad(until.day)}`
      if (compare) return 'stop'
    }

    const dateKey = `${local.year}-${pad(local.month)}-${pad(local.day)}`
    if (exceptions.has(dateKey) || exceptions.has(formatLocal(local))) return 'continue'

    produced += 1
    if (rule.count !== undefined && produced > rule.count) return 'stop'

    const startsAt = zonedWallTimeToUtc(local, series.timeZone)
    if (options.to && startsAt >= options.to) {
      // Later occurrences are later still; nothing after this can qualify.
      return 'stop'
    }
    if (options.from && startsAt < options.from) return 'continue'

    results.push({
      localStart: formatLocal(local),
      startsAt,
      endsAt: new Date(startsAt.getTime() + series.durationMinutes * 60_000),
    })
    return results.length >= limit ? 'stop' : 'continue'
  }

  if (rule.freq === 'DAILY') {
    for (let cursor = start; candidates < MAX_CANDIDATES; cursor = addDays(cursor, interval)) {
      candidates += 1
      if (consider(cursor) === 'stop') break
    }
    return results
  }

  if (rule.freq === 'WEEKLY') {
    const days = (rule.byDay ?? [weekdayOf(start.year, start.month, start.day)]).map(
      (entry) => normalizeByDay(entry).day,
    )
    // Walk day by day from the start, keeping only the named weekdays in weeks
    // that fall on the interval.
    for (let cursor = start; candidates < MAX_CANDIDATES; cursor = addDays(cursor, 1)) {
      candidates += 1
      const weekday = weekdayOf(cursor.year, cursor.month, cursor.day)
      if (!days.includes(weekday)) continue
      if (weeksBetween(start, cursor, weekStart) % interval !== 0) continue
      if (consider(cursor) === 'stop') break
    }
    return results
  }

  // MONTHLY
  const useStartDay = (rule.byDay ?? []).length === 0 && (rule.byMonthDay ?? []).length === 0
  for (let month = start, guard = 0; guard < 600; month = addMonths(month, interval), guard += 1) {
    const days = useStartDay ? [start.day] : monthlyDays(rule, month.year, month.month)
    let stop = false
    for (const day of days) {
      if (day > daysInMonth(month.year, month.month)) continue
      const local = { ...month, day }
      // Months before the series start are skipped rather than emitted.
      if (
        `${local.year}-${pad(local.month)}-${pad(local.day)}` <
        `${start.year}-${pad(start.month)}-${pad(start.day)}`
      ) {
        continue
      }
      candidates += 1
      if (consider(local) === 'stop') {
        stop = true
        break
      }
    }
    if (stop || candidates >= MAX_CANDIDATES) break
  }
  return results
}

/** The next occurrence at or after `after`, or null if the series has ended. */
export function nextOccurrence(
  series: RecurringSeries,
  after: Date = new Date(),
): Occurrence | null {
  return expandSeries(series, { from: after, limit: 1 })[0] ?? null
}

// ---- description ---------------------------------------------------------------

const DAY_NAMES: Record<Weekday, string> = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
}

const ORDINALS = ['', 'first', 'second', 'third', 'fourth', 'fifth']

/** "Every second Tuesday" — for group cards and meeting lists. */
export function describeRecurrence(rule: RecurrenceRule): string {
  const interval = Math.max(1, rule.interval ?? 1)
  const every = interval === 1 ? 'Every' : interval === 2 ? 'Every other' : `Every ${interval}`

  if (rule.freq === 'DAILY') return interval === 1 ? 'Every day' : `${every} days`

  if (rule.freq === 'WEEKLY') {
    const days = (rule.byDay ?? []).map((entry) => DAY_NAMES[normalizeByDay(entry).day])
    if (days.length === 0) return interval === 1 ? 'Every week' : `${every} weeks`
    return `${every} ${days.join(', ')}`
  }

  const nthDays = (rule.byDay ?? []).map((entry) => {
    const { nth, day } = normalizeByDay(entry)
    if (nth === 0) return `every ${DAY_NAMES[day]}`
    if (nth === -1) return `the last ${DAY_NAMES[day]}`
    return `the ${ORDINALS[nth] ?? `${nth}th`} ${DAY_NAMES[day]}`
  })
  if (nthDays.length > 0) return `${nthDays.join(' and ')} of the month`
  if ((rule.byMonthDay ?? []).length > 0) {
    return `Day ${(rule.byMonthDay ?? []).join(', ')} of the month`
  }
  return interval === 1 ? 'Every month' : `${every} months`
}

/**
 * The same sentence for a whole series, with the day and time filled in from
 * its start.
 *
 * A weekly rule with no BYDAY recurs on the start's weekday (RFC 5545 §3.3.10),
 * so "Every week" is true but useless on a group card — the expander already
 * knows it means Tuesday, and so should the label.
 */
export function describeSeries(series: RecurringSeries): string {
  const start = parseLocal(series.start)
  const rule = series.rule
  const implied: RecurrenceRule =
    rule.freq === 'WEEKLY' && (rule.byDay ?? []).length === 0
      ? { ...rule, byDay: [weekdayOf(start.year, start.month, start.day)] }
      : rule.freq === 'MONTHLY' &&
          (rule.byDay ?? []).length === 0 &&
          (rule.byMonthDay ?? []).length === 0
        ? { ...rule, byMonthDay: [start.day] }
        : rule

  const time = formatWallTime(start)
  return time ? `${describeRecurrence(implied)} at ${time}` : describeRecurrence(implied)
}

/** 19:00 → "7:00 PM". Midnight reads as an all-day series, so it is omitted. */
function formatWallTime(local: LocalDateTime): string | null {
  if (local.hour === 0 && local.minute === 0) return null
  const hour12 = local.hour % 12 === 0 ? 12 : local.hour % 12
  return `${hour12}:${pad(local.minute)} ${local.hour < 12 ? 'AM' : 'PM'}`
}

// ---- validation -----------------------------------------------------------------

const weekdaySchema = z.enum(WEEKDAYS)

export const recurrenceRuleSchema = z.object({
  freq: z.enum(['DAILY', 'WEEKLY', 'MONTHLY']),
  interval: z.number().int().min(1).max(52).optional(),
  byDay: z
    .array(
      z.union([
        weekdaySchema,
        z.object({ nth: z.number().int().min(-5).max(5), day: weekdaySchema }),
      ]),
    )
    .max(7)
    .optional(),
  byMonthDay: z.array(z.number().int().min(-31).max(31)).max(31).optional(),
  count: z.number().int().min(1).max(500).optional(),
  until: z.string().regex(LOCAL_PATTERN, 'Use YYYY-MM-DD or YYYY-MM-DDTHH:mm').optional(),
  weekStart: weekdaySchema.optional(),
})

export const recurringSeriesSchema = z.object({
  start: z.string().regex(LOCAL_PATTERN, 'Use YYYY-MM-DDTHH:mm'),
  durationMinutes: z
    .number()
    .int()
    .min(5)
    .max(24 * 60),
  timeZone: z.string().min(1).refine(isValidTimeZone, 'Not a known timezone'),
  rule: recurrenceRuleSchema,
  exceptions: z.array(z.string().regex(LOCAL_PATTERN)).max(200).optional(),
})

export function isValidTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone })
    return true
  } catch {
    return false
  }
}
